import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { GridService } from '@core/services';
import { EnvironmentService, EventService, ListService, fromList } from '@core/services';
import { currentTimeInSeconds } from '@util/list';

import { MultipleListOfValuesResponse } from '#types/list';

import { CONSTANTS } from '@shipment/constants';
import { ShipmentService } from '@shipment/services';

import * as ShipmentActions from './shipment.actions';

@Injectable()
export class ShipmentEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private gridService: GridService,
    private shipmentService: ShipmentService,
    private environmentService: EnvironmentService,
    private listService: ListService,
    private eventService: EventService
  ) {}

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ShipmentActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectShipmentLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(ShipmentActions.RequestShipmentLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  RequestShipmentLists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShipmentActions.RequestShipmentLists),
      exhaustMap(() => {
        return this.listService.getListOfValues<MultipleListOfValuesResponse>('shipment').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((lists) => ShipmentActions.RequestShipmentListsSuccess({ lists })),
          catchError((error) => of(ShipmentActions.RequestShipmentListsFailure(error)))
        );
      })
    );
  });

  SupplierLookupRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ShipmentActions.SupplierLookupDialogRequestData,
        ShipmentActions.SupplierLookupDialogRequestDataWithFilters,
        ShipmentActions.SupplierLookupDialogRequestDataWithParameters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.shipmentService.getSupplierLookup({ filters, page, rows, sorts }).pipe(
          map((data) => ShipmentActions.SupplierLookupDialogRequestDataSuccess({ id, data })),
          catchError((_error) => of(ShipmentActions.SupplierLookupDialogRequestDataFailure({ id })))
        )
      )
    );
  });
}
